import type { z } from 'zod';
import type {
  FindingDefinitionDetailSchema,
  FindingInstanceDetailSchema,
  FindingSiblingsSchema,
} from '@contract/findingDetail';
import type { BulkTriageResultEnvelope } from '@contract/bulkTriage';
import type {
  GroupedOrgFindingRowSchema,
  OrgFindingRowSchema,
  OrgFindingsGroupedListEnvelope,
  OrgFindingsListEnvelope,
  OrgFindingsFacetsEnvelope,
} from '@contract/findingsList';
import api, { apiClient } from './api';
import type { FindingSeverity, FindingSource } from './auditService';

/**
 * REV-133: client for the org-wide findings endpoint. Returns every
 * open finding across every Context and AuditSession in the org.
 */

export type FindingStatus =
  | 'open'
  | 'acknowledged'
  | 'in_progress'
  | 'resolved'
  | 'false_positive'
  | 'wont_fix';

/**
 * The set the issues-breakdown report counts (OPEN_FINDING_STATUSES on the
 * API). Distinct from "everything except resolved", which the findings list
 * defaults to and which also includes false_positive and wont_fix — so a
 * report row linking here without it lands on a bigger number than it showed.
 */
export const UNRESOLVED_STATUSES = 'open,acknowledged,in_progress';

/**
 * Every status, including resolved and dispositioned.
 *
 * REV-720: what a scan-run view has to ask for. "The findings this run found"
 * does not stop being true because someone later resolved one, and the list's
 * own default — anything but resolved — would quietly shrink a past run's
 * results as the team worked through them.
 */
export const FINDING_STATUSES: FindingStatus[] = [
  'open',
  'acknowledged',
  'in_progress',
  'resolved',
  'false_positive',
  'wont_fix',
];

export const ALL_FINDING_STATUSES = FINDING_STATUSES.join(',');

export interface OrgFindingsFilters {
  source?: FindingSource | 'all';
  severity?: FindingSeverity;
  /** One status, or a comma-separated list (see UNRESOLVED_STATUSES). */
  status?: FindingStatus | string;
  context_id?: string;
  wcag_sc_id?: string;
  category?: string;
  /** A | AA | AAA | unspecified — 'unspecified' means the rule declares none. */
  wcag_level?: string;
  rule_id?: string;
  /**
   * REV-720: restrict to the findings one scan run produced. This is what lets
   * the scan-results table and /findings be the same table reading the same
   * endpoint rather than two implementations of one list. Manual findings are
   * excluded server-side — a TestEvaluation has no scan run.
   */
  scan_run_id?: string;
  /** REV-712: order before paging. Absent means severity, most severe first. */
  sort?: 'severity' | 'rule';
  direction?: 'asc' | 'desc';
  /**
   * REV-1097: only the per-page rows of one grouped issue, by the `signature`
   * a grouped row carries. How a grouped row lists the pages it covers.
   */
  signature?: string;
  limit?: number;
  offset?: number;
}

/**
 * REV-821: a row of the org findings list, from the vendored contract, which
 * revenant-api checks against its real routes over findings the real scan and
 * a real audit verdict wrote. An automated row and a manual one differ: only
 * a manual row has an audit, and only an automated one has an element and a
 * page, so narrow on `source` before reading either.
 */
export type OrgFinding = z.infer<typeof OrgFindingRowSchema>;

/**
 * REV-909: what a bulk action DID. The API skips a finding already confirmed,
 * dispositioned or resolved (REV-509), so `processed` can be fewer than the ids
 * sent; report it, never the count sent.
 */
export type BulkTriageResult = z.infer<typeof BulkTriageResultEnvelope>['data'];

/** Distinct filter values present in the org's findings (for the dropdowns). */
export type OrgFindingsFacets = z.infer<typeof OrgFindingsFacetsEnvelope>['data'];

export interface OrgFindingsPage {
  items: OrgFinding[];
  pagination: z.infer<typeof OrgFindingsListEnvelope>['pagination'];
}

/**
 * REV-1097: one row per issue rather than per page. The rule, element and
 * tracked place a finding shares with others, with how many pages and
 * findings it covers; `severity` is the worst and `status` the least settled
 * of them. Defined by revenant-api's src/utils/findingGroups.js.
 */
export type GroupedOrgFinding = z.infer<typeof GroupedOrgFindingRowSchema>;

/** `pagination.total` counts groups, not findings. */
export interface GroupedOrgFindingsPage {
  items: GroupedOrgFinding[];
  pagination: z.infer<typeof OrgFindingsGroupedListEnvelope>['pagination'];
}

/**
 * REV-821: the finding detail and siblings types come from the vendored
 * contract, which revenant-api checks against its real routes over findings
 * the real scan wrote. The hand-typed versions had drifted: `wcag_level` is
 * null for a rule the catalog does not know (an external tool's), and
 * `documentation_title` is always present (null when the catalog has none).
 */
/** REV-134: GET /organizations/:orgId/finding-instances/:id */
export type FindingDefinitionDetail = z.infer<typeof FindingDefinitionDetailSchema>;
export type FindingInstanceDetail = z.infer<typeof FindingInstanceDetailSchema>;

/**
 * REV-509: false positive and won't fix accept DIFFERENT reason vocabularies.
 *
 * A false positive asserts the finding is WRONG (the tool erred); won't fix
 * concedes it is RIGHT but declines to act. They previously shared one list,
 * which let "by_design" — an admission the finding is real — be filed as a
 * false positive. These mirror src/constants/dispositionReasons.js in
 * revenant-api; the API rejects anything outside the matching list.
 */

/** Reasons accepted by POST /findings/:id/false-positive */
export type FalsePositiveReason =
  | 'incorrect_detection'
  | 'incorrect_wcag_mapping'
  | 'context_specific'
  | 'testing_artifact'
  | 'other';

/** Reasons accepted by POST /findings/:id/wont-fix */
export type WontFixReason = 'by_design' | 'third_party' | 'not_applicable' | 'duplicate' | 'other';

/** REV-539: the other elements the same rule fails on in the tracked place. */
export type FindingSiblings = z.infer<typeof FindingSiblingsSchema>;
export type FindingSiblingElement = FindingSiblings['items'][number];

/**
 * The query string for the org findings list. Shared by the per-page and the
 * grouped list (REV-1097), so the two can never send different filters.
 */
function listParams(filters: OrgFindingsFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (filters.source) params.source = filters.source;
  if (filters.severity) params.severity = filters.severity;
  if (filters.status) params.status = filters.status;
  if (filters.context_id) params.context_id = filters.context_id;
  if (filters.wcag_sc_id) params.wcag_sc_id = filters.wcag_sc_id;
  if (filters.category) params.category = filters.category;
  if (filters.wcag_level) params.wcag_level = filters.wcag_level;
  if (filters.rule_id) params.rule_id = filters.rule_id;
  if (filters.scan_run_id) params.scan_run_id = filters.scan_run_id;
  if (filters.sort) params.sort = filters.sort;
  if (filters.direction) params.direction = filters.direction;
  if (filters.signature) params.signature = filters.signature;
  if (filters.limit !== undefined) params.limit = filters.limit;
  if (filters.offset !== undefined) params.offset = filters.offset;
  return params;
}

export const findingsService = {
  async listForOrg(orgId: string, filters: OrgFindingsFilters = {}): Promise<OrgFindingsPage> {
    const res = await apiClient.get<z.infer<typeof OrgFindingsListEnvelope>>(
      `/v1/organizations/${orgId}/findings`,
      listParams(filters)
    );

    return { items: res.data, pagination: res.pagination };
  },

  /**
   * REV-1097: the same list, one row per issue across pages
   * (`group_by=signature`). Takes the same filters, so a group's counts are
   * those of the rows the same filters list ungrouped.
   */
  async listGroupsForOrg(
    orgId: string,
    filters: OrgFindingsFilters = {}
  ): Promise<GroupedOrgFindingsPage> {
    const res = await apiClient.get<z.infer<typeof OrgFindingsGroupedListEnvelope>>(
      `/v1/organizations/${orgId}/findings`,
      { ...listParams(filters), group_by: 'signature' }
    );

    return { items: res.data, pagination: res.pagination };
  },

  /**
   * The name to save a findings export under, matching what the API puts in
   * its Content-Disposition: `contextId || organizationId`, truncated to eight
   * characters. Filtering to one tracked place names the file after THAT
   * place, so exporting two places gives two distinct files instead of one
   * name written twice.
   *
   * This duplicates a server rule, which is not ideal. The server's own name
   * cannot be read: Content-Disposition is not CORS-safelisted and the API
   * sends no Access-Control-Expose-Headers, so the browser withholds it from
   * JS. Removing this duplication needs the API to expose that header first.
   */
  exportFilename(orgId: string, contextId?: string): string {
    return `findings-${String(contextId || orgId).substring(0, 8)}.csv`;
  },

  /**
   * REV-816: the org's findings as a CSV Blob (streamed server-side).
   * GET /v1/organizations/:orgId/findings/export?format=csv
   *
   * The raw axios instance with responseType 'blob', so the request still
   * carries the Bearer token (the same reason the report exports use it) and
   * the caller gets the bytes to turn into a download. The same filters the
   * list uses are forwarded EXCEPT limit/offset/sort — an export is the whole
   * filtered set, not one page — so a narrowed list exports what it showed.
   */
  async exportCsv(orgId: string, filters: OrgFindingsFilters = {}): Promise<Blob> {
    const params: Record<string, string> = { format: 'csv' };
    if (filters.source) params.source = filters.source;
    if (filters.severity) params.severity = filters.severity;
    if (filters.status) params.status = String(filters.status);
    if (filters.context_id) params.context_id = filters.context_id;
    if (filters.wcag_sc_id) params.wcag_sc_id = filters.wcag_sc_id;
    if (filters.category) params.category = filters.category;
    if (filters.wcag_level) params.wcag_level = filters.wcag_level;
    if (filters.rule_id) params.rule_id = filters.rule_id;
    if (filters.scan_run_id) params.scan_run_id = filters.scan_run_id;
    // REV-1097: an open group narrows the export as it narrows the list.
    if (filters.signature) params.signature = filters.signature;

    const res = await api.get<Blob>(`/v1/organizations/${orgId}/findings/export`, {
      params,
      responseType: 'blob',
    });
    return res.data;
  },

  /** Distinct categories + WCAG SCs present in the org's findings (for filter dropdowns). */
  async getFacets(orgId: string): Promise<OrgFindingsFacets> {
    const res = await apiClient.get<z.infer<typeof OrgFindingsFacetsEnvelope>>(
      `/v1/organizations/${orgId}/findings/facets`
    );
    return res.data;
  },

  async getInstance(orgId: string, id: string): Promise<FindingInstanceDetail> {
    const res = await apiClient.get<{ success: boolean; data: FindingInstanceDetail }>(
      `/v1/organizations/${orgId}/finding-instances/${id}`
    );
    return res.data;
  },

  /**
   * REV-539: the other elements this same rule fails on in the tracked place.
   * Counts are exact and independent of `limit` — the list is a navigation aid,
   * the number is the point.
   */
  async getSiblings(
    orgId: string,
    id: string,
    opts: { limit?: number; offset?: number } = {}
  ): Promise<FindingSiblings> {
    const params: Record<string, number> = {};
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.offset !== undefined) params.offset = opts.offset;
    const res = await apiClient.get<{ success: boolean; data: FindingSiblings }>(
      `/v1/organizations/${orgId}/finding-instances/${id}/siblings`,
      params
    );
    return res.data;
  },

  async confirmFinding(orgId: string, id: string, input: { notes?: string } = {}): Promise<void> {
    await apiClient.post(`/v1/findings/${id}/confirm`, {
      organizationId: orgId,
      ...(input.notes ? { notes: input.notes } : {}),
    });
  },

  /**
   * REV-509: `notes` is REQUIRED — a false-positive claim must be justified in
   * writing, and the API rejects a blank note with 400. Typing it as required
   * makes that a compile error here rather than a runtime failure.
   */
  async markFalsePositive(
    orgId: string,
    id: string,
    input: { reason: FalsePositiveReason; notes: string }
  ): Promise<void> {
    await apiClient.post(`/v1/findings/${id}/false-positive`, {
      organizationId: orgId,
      reason: input.reason,
      notes: input.notes,
    });
  },

  /** REV-220: mark a finding as won't fix (reason required). */
  async markWontFix(
    orgId: string,
    id: string,
    input: { reason: WontFixReason; notes?: string }
  ): Promise<void> {
    await apiClient.post(`/v1/findings/${id}/wont-fix`, {
      organizationId: orgId,
      reason: input.reason,
      ...(input.notes ? { notes: input.notes } : {}),
    });
  },

  /** REV-220: mark a finding as resolved (fixed). Optional notes, no reason. */
  async resolveFinding(orgId: string, id: string, notes?: string): Promise<void> {
    await apiClient.post(`/v1/findings/${id}/resolve`, {
      organizationId: orgId,
      ...(notes ? { notes } : {}),
    });
  },

  /** REV-220: reopen a resolved/false-positive/won't-fix finding. */
  async reopenFinding(orgId: string, id: string, notes?: string): Promise<void> {
    await apiClient.post(`/v1/findings/${id}/reopen`, {
      organizationId: orgId,
      ...(notes ? { notes } : {}),
    });
  },

  /**
   * REV-220: bulk-mark multiple findings as false positive.
   * REV-509: `notes` is required here too — the bulk endpoint enforces the same
   * justification rule, so an optional note would just produce a 400.
   */
  async bulkMarkFalsePositive(
    orgId: string,
    findingInstanceIds: string[],
    reason: FalsePositiveReason,
    notes: string
  ): Promise<BulkTriageResult> {
    const res = await apiClient.post<z.infer<typeof BulkTriageResultEnvelope>>(
      '/v1/feedback/bulk/false-positive',
      { organizationId: orgId, findingInstanceIds, reason, notes }
    );
    return res.data;
  },

  /** REV-220: bulk-confirm multiple findings as valid. */
  async bulkConfirm(
    orgId: string,
    findingInstanceIds: string[],
    notes?: string
  ): Promise<BulkTriageResult> {
    const res = await apiClient.post<z.infer<typeof BulkTriageResultEnvelope>>(
      '/v1/feedback/bulk/confirm',
      { organizationId: orgId, findingInstanceIds, ...(notes ? { notes } : {}) }
    );
    return res.data;
  },
};
